import { useRef, useState, useCallback, useEffect } from "react";
import { useAuth } from "./useAuth";
import { EXTERNAL_SUPABASE_URL } from "@/integrations/external-supabase/client";

const TRANSCRIBE_URL = `${EXTERNAL_SUPABASE_URL}/functions/v1/kin-voice-transcribe`;

// Silence detection tuning
const SPEECH_THRESHOLD = 20;       // 0–255 amplitude — above this counts as speech
const SILENCE_THRESHOLD = 12;      // below this counts as silence
// Tuned on device July 3 — moms pause mid-thought while cooking/multitasking.
// If users report getting cut off: raise. If they report long dead air before send: lower.
const SILENCE_DURATION_MS = 4000;  // 4s of continuous silence → auto-send
const MAX_RECORDING_MS = 30000;    // 30s hard cap in case user walks away
const MIN_BEFORE_SILENCE_MS = 400; // don't start checking silence in first 400ms
// Below this, there's not enough audio for real speech — Whisper hallucinates
// stock phrases (e.g. "thank you for watching") on near-silent clips this
// short. Discard client-side: no API call, no text, no error. Also saves
// the API cost of transcribing accidental taps.
// 700ms broke TodayTab/TasksTab's quick calendar-phrase dictation ("swim
// Tuesday 4pm") — short but real utterances were landing under the floor.
// 300ms still catches genuine reflex double-taps (typically well under it)
// without eating legitimate short phrases. (July 9 2026 fix.)
const MIN_RECORDING_MS = 300;

// Single quick, soft tick — clearly distinct from the star's two-note
// melodic chime (useRealtimeVoice.ts's playChime). Dictation start/stop
// gets one short blip each, not a melody. Fire-and-forget (not awaited).
function playTick(type: "start" | "stop"): void {
  try {
    const AudioContextClass = window.AudioContext ?? (window as any).webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();
    const freq = type === "start" ? 880 : 660;
    const dur = 0.07;
    const t = ctx.currentTime + 0.01;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sine";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.18, t + 0.005);
    gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
    osc.start(t);
    osc.stop(t + dur);
    setTimeout(() => { try { ctx.close(); } catch (_) {} }, (dur + 0.05) * 1000);
  } catch (_) {
    // Web Audio unavailable — silent no-op, never fatal
  }
}

export interface UseVoiceInputReturn {
  isRecording: boolean;
  isProcessing: boolean;
  /** Pass onAutoStop for hands-free (silence detection) mode.
   *  Omit for one-shot push-to-tap mode (ChatInput mic button). */
  startRecording: (onAutoStop?: (transcript: string | null) => void) => Promise<void>;
  stopRecording: () => Promise<string | null>;
  toggleRecording: () => Promise<string | null>;
  error: string | null;
  clearError: () => void;
  isSupported: boolean;
}

export function useVoiceInput(): UseVoiceInputReturn {
  const { session } = useAuth();
  const sessionRef = useRef(session);
  useEffect(() => { sessionRef.current = session; }, [session]);

  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingStartRef = useRef(0);
  // Ref mirrors isRecording state so requestAnimationFrame loops can check it
  // without capturing a stale closure value
  const isRecordingRef = useRef(false);

  const stopTracks = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => {
    return () => {
      isRecordingRef.current = false;
      stopTracks();
      mediaRecorderRef.current = null;
    };
  }, [stopTracks]);

  const stopRecording = useCallback((): Promise<string | null> => {
    return new Promise((resolve) => {
      isRecordingRef.current = false; // signal the rAF loop to exit
      const recorder = mediaRecorderRef.current;
      if (!recorder || recorder.state === "inactive") {
        setIsRecording(false);
        resolve(null);
        return;
      }

      recorder.onstop = async () => {
        stopTracks();
        setIsRecording(false);
        playTick("stop");

        const durationMs = Date.now() - recordingStartRef.current;
        if (durationMs < MIN_RECORDING_MS) {
          // Too short for real speech — discard silently, no API call.
          chunksRef.current = [];
          resolve(null);
          return;
        }

        setIsProcessing(true);
        try {
          const blob = new Blob(chunksRef.current, {
            type: recorder.mimeType || "audio/webm",
          });
          const formData = new FormData();
          formData.append("audio", blob, "recording.webm");

          const resp = await fetch(TRANSCRIBE_URL, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${sessionRef.current?.access_token ?? ""}`,
            },
            body: formData,
          });

          if (!resp.ok) throw new Error(`Transcription error ${resp.status}`);
          const data = await resp.json();
          const text = (data.text ?? "").trim();
          resolve(text || null);
        } catch {
          setError("Couldn't transcribe audio. Please try again.");
          resolve(null);
        } finally {
          setIsProcessing(false);
        }
      };

      recorder.stop();
    });
  }, [stopTracks]);

  // Stable ref so rAF loops always call the latest stopRecording
  // without capturing a potentially-stale closure
  const stopRecordingRef = useRef(stopRecording);
  useEffect(() => { stopRecordingRef.current = stopRecording; }, [stopRecording]);

  const startRecording = useCallback(
    async (onAutoStop?: (transcript: string | null) => void) => {
      try {
        chunksRef.current = [];
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        streamRef.current = stream;

        const mimeType = MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : MediaRecorder.isTypeSupported("audio/mp4")
          ? "audio/mp4"
          : "";
        const recorder = mimeType
          ? new MediaRecorder(stream, { mimeType })
          : new MediaRecorder(stream);
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };
        recorder.start();
        recordingStartRef.current = Date.now();
        isRecordingRef.current = true;
        setIsRecording(true);
        setError(null);
        playTick("start");

        // Hands-free silence detection — only engaged when caller passes onAutoStop.
        // One-shot mic button callers omit onAutoStop and use stopRecording() manually.
        if (onAutoStop) {
          let audioCtx: AudioContext | null = null;
          try {
            const AudioContextClass =
              window.AudioContext ?? (window as any).webkitAudioContext;
            audioCtx = new AudioContextClass();
            if (audioCtx.state === "suspended") await audioCtx.resume();

            const analyser = audioCtx.createAnalyser();
            analyser.fftSize = 256;
            const source = audioCtx.createMediaStreamSource(stream);
            source.connect(analyser);

            const dataArray = new Uint8Array(analyser.frequencyBinCount);
            let hasSpokeYet = false;
            let silenceStart: number | null = null;
            const recordingStart = Date.now();

            const checkSilence = () => {
              // Recording was stopped externally (deactivate or error) — clean up
              if (!isRecordingRef.current) {
                audioCtx?.close();
                return;
              }

              const elapsed = Date.now() - recordingStart;

              // Hard safety cap — stop after MAX_RECORDING_MS regardless
              if (elapsed > MAX_RECORDING_MS) {
                audioCtx?.close();
                stopRecordingRef.current().then(onAutoStop);
                return;
              }

              // Skip silence checking for the first MIN_BEFORE_SILENCE_MS
              // so we don't fire instantly on a quiet environment at startup
              if (elapsed >= MIN_BEFORE_SILENCE_MS) {
                analyser.getByteFrequencyData(dataArray);
                const avg =
                  dataArray.reduce((a, b) => a + b, 0) / dataArray.length;

                if (avg >= SPEECH_THRESHOLD) {
                  hasSpokeYet = true;
                  silenceStart = null; // speech detected — reset silence clock
                } else if (hasSpokeYet) {
                  // We heard speech earlier; now tracking how long silence lasts
                  if (silenceStart === null) {
                    silenceStart = Date.now();
                  } else if (Date.now() - silenceStart >= SILENCE_DURATION_MS) {
                    // 2.5 seconds of silence after speech → auto-send
                    audioCtx?.close();
                    stopRecordingRef.current().then(onAutoStop);
                    return; // exit the rAF loop
                  }
                }
              }

              requestAnimationFrame(checkSilence);
            };

            requestAnimationFrame(checkSilence);
          } catch {
            // AudioContext unavailable (restricted WKWebView config) —
            // fall back to a fixed 5-second recording window
            audioCtx?.close();
            setTimeout(() => {
              if (isRecordingRef.current) {
                stopRecordingRef.current().then(onAutoStop);
              }
            }, 5000);
          }
        }
      } catch {
        setError(
          "Microphone access denied. Please allow microphone access and try again."
        );
        stopTracks();
      }
    },
    [stopTracks]
  );

  const toggleRecording = useCallback(async (): Promise<string | null> => {
    if (isRecording) return stopRecording();
    await startRecording();
    return null;
  }, [isRecording, startRecording, stopRecording]);

  const clearError = useCallback(() => setError(null), []);

  return {
    isRecording,
    isProcessing,
    startRecording,
    stopRecording,
    toggleRecording,
    error,
    clearError,
    isSupported:
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia,
  };
}
